import React from 'react';
import {
  AlertTriangle,
  CheckCircle,
  Target,
} from 'lucide-react';

export const ResultsStep = ({ processingResult }) => {
  const loadedFromHistory = Boolean(processingResult?.loaded_from_history);
  const positiveCount = Number(processingResult?.test_cases?.positive_count || 0);
  const negativeCount = Number(processingResult?.test_cases?.negative_count || 0);
  const totalCount = Number(processingResult?.test_cases?.total_count || positiveCount + negativeCount);
  const hasGaps = Array.isArray(processingResult?.coverage_gaps) && processingResult.coverage_gaps.length > 0;
  const hasFocus = Array.isArray(processingResult?.approved_focus) && processingResult.approved_focus.length > 0;
  const hasGlobalConstraints =
    Array.isArray(processingResult?.approved_global_constraints)
    && processingResult.approved_global_constraints.length > 0;

  return (
    <div className="space-y-5">
      <div className="mx-auto max-w-5xl rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loadedFromHistory && (
          <div className="border-b border-blue-200 bg-blue-50 px-6 py-3">
            <p className="text-sm font-medium text-blue-900">
              Loaded the latest generated output for this section.
            </p>
          </div>
        )}
        <div className="bg-gradient-to-r from-sky-50 to-cyan-50 border-b border-slate-200 px-6 py-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-white border border-emerald-200 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">Generation Complete</p>
                <p className="text-sm text-slate-700 mt-0.5">
                  Module: <span className="font-semibold">{processingResult.section_title}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Total Cases</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{totalCount}</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-emerald-700">Positive</p>
              <p className="text-2xl font-bold text-emerald-800 mt-1">{positiveCount}</p>
            </div>
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-rose-700">Negative</p>
              <p className="text-2xl font-bold text-rose-800 mt-1">{negativeCount}</p>
            </div>
          </div>

          {hasFocus && (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
              <p className="text-sm font-semibold text-indigo-900 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Applied Focus Areas
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {processingResult.approved_focus.map((focus, idx) => (
                  <span
                    key={`${focus}-${idx}`}
                    className="px-2.5 py-1 rounded-full bg-white border border-indigo-200 text-indigo-800 text-xs font-medium"
                  >
                    {focus}
                  </span>
                ))}
              </div>
            </div>
          )}

          {hasGlobalConstraints && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Global Constraints Applied
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {processingResult.approved_global_constraints.map((item, idx) => (
                  <span
                    key={`${item}-${idx}`}
                    className="px-2.5 py-1 rounded-full bg-white border border-emerald-200 text-emerald-800 text-xs font-medium"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {hasGaps && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Coverage Gaps
              </p>
              <ul className="text-sm text-amber-900 space-y-1.5">
                {processingResult.coverage_gaps.map((gap, idx) => (
                  <li key={idx} className="leading-relaxed">• {gap}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
